import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { XMarkIcon, MagnifyingGlassIcon, CheckCircleIcon, ExclamationCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import {
  discoveryMatch,
  subscribeToApp,
  submitJob,
  getBulkJobStatus,
  searchWinGetPackages,
  searchChocolateyPackages,
  type MatchCandidate,
  type MatchSource,
  type DiscoveryMatchResult,
} from '../services/api'
import type { JobStatusResponse } from '../types/job'
import { isTerminalStatus } from '../types/job'
import { errorText } from '../utils/errorText'
import PackagingEntitlementNotice from './PackagingEntitlementNotice'

type Step = 'matching' | 'review' | 'submitting' | 'progress'

interface SelectedApp {
  id: string
  app_name: string
  publisher: string | null
  version: string | null
  arch?: string | null
  management_state?: string | null
  detected_app_id?: string | null
}

interface AddToCatalogModalProps {
  isOpen: boolean
  onClose: () => void
  apps: SelectedApp[]
}

interface ReviewRow {
  app: SelectedApp
  result: DiscoveryMatchResult
  selected: MatchCandidate | null
  included: boolean
  manualSearching: boolean
  manualResults: MatchCandidate[]
}

interface SubmittedItem {
  appName: string
  source: MatchSource
  action: MatchCandidate['action']
  label: string          // package id or matched catalog name
  jobId: string | null   // packaging jobs only
  appId: string | null   // for navigation (catalog app id)
  error: string | null
  immediate: 'subscribed' | 'already' | null
  duplicate: boolean
}

const sourceMeta: Record<MatchSource, { label: string; cls: string }> = {
  public_catalog: { label: 'Public Catalog', cls: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400' },
  private_catalog: { label: 'Private Catalog', cls: 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-400' },
  winget: { label: 'WinGet', cls: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400' },
  chocolatey: { label: 'Chocolatey', cls: 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-400' },
}

/** What submitting this match actually does, said before the user presses Add. */
const outcomeText: Record<MatchCandidate['action'], string> = {
  package: 'Will be packaged from this source',
  subscribe: 'Adds a shortcut to the certified public package',
  already_in_catalog: 'Already in your library — no conversion needed',
}

const confidenceColors: Record<string, string> = {
  high: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400',
  medium: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-400',
  low: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400',
  none: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
}

function candKey(c: MatchCandidate): string {
  const ref = c.ref.public_app_id || c.ref.tenant_app_id || c.ref.package_id || c.name
  return `${c.source}:${ref}`
}

export default function AddToCatalogModal({ isOpen, onClose, apps }: AddToCatalogModalProps) {
  const [step, setStep] = useState<Step>('matching')
  const [reviewRows, setReviewRows] = useState<ReviewRow[]>([])
  const [submittedItems, setSubmittedItems] = useState<SubmittedItem[]>([])
  const [jobStatuses, setJobStatuses] = useState<Record<string, JobStatusResponse>>({})
  const [submitProgress, setSubmitProgress] = useState(0)
  const [matchError, setMatchError] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Step 1: match on open
  useEffect(() => {
    if (!isOpen || apps.length === 0) return

    setStep('matching')
    setMatchError(null)
    setReviewRows([])
    setSubmittedItems([])
    setJobStatuses({})

    const doMatch = async () => {
      try {
        const { results } = await discoveryMatch(
          apps.map(a => ({
            id: a.id,
            name: a.app_name,
            version: a.version,
            arch: a.arch ?? null,
            publisher: a.publisher,
            management_state: a.management_state ?? null,
            detected_app_id: a.detected_app_id ?? null,
          }))
        )

        const rows: ReviewRow[] = apps.map((app, i) => {
          const result = results[i]
          const selected = result?.auto_selected || result?.candidates?.[0] || null
          return {
            app,
            result: result || { app: { id: app.id, name: app.app_name }, auto_selected: null, needs_prompt: true, confidence: 'none', candidates: [] },
            selected,
            included: !!selected,
            manualSearching: false,
            manualResults: [],
          }
        })

        setReviewRows(rows)
        setStep('review')
      } catch (err) {
        setMatchError((err as Error).message || 'Failed to match packages')
        setStep('review')
      }
    }

    doMatch()
  }, [isOpen, apps])

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  const handleManualSearch = useCallback(async (index: number, query: string) => {
    if (!query.trim()) return
    setReviewRows(prev => {
      const next = [...prev]
      next[index] = { ...next[index], manualSearching: true }
      return next
    })

    try {
      const [wingetRes, chocoRes] = await Promise.allSettled([
        searchWinGetPackages(query, 5),
        searchChocolateyPackages(query, 5),
      ])
      const toCandidate = (p: { id: string; name: string; publisher: string; version: string }, source: MatchSource): MatchCandidate => ({
        source,
        source_priority: source === 'winget' ? 3 : 4,
        match_type: 'close',
        score: 0,
        arch_compatible: true,
        version_match: false,
        name: p.name,
        version: p.version,
        arch: null,
        publisher: p.publisher,
        ref: { package_id: p.id },
        action: 'package',
      })
      const wMatches = wingetRes.status === 'fulfilled' ? wingetRes.value.packages.map(p => toCandidate(p, 'winget')) : []
      const cMatches = chocoRes.status === 'fulfilled' ? chocoRes.value.packages.map(p => toCandidate(p, 'chocolatey')) : []

      setReviewRows(prev => {
        const next = [...prev]
        next[index] = { ...next[index], manualSearching: false, manualResults: [...wMatches, ...cMatches].slice(0, 10) }
        return next
      })
    } catch {
      setReviewRows(prev => {
        const next = [...prev]
        next[index] = { ...next[index], manualSearching: false }
        return next
      })
    }
  }, [])

  const handleSelectCandidate = useCallback((index: number, cand: MatchCandidate) => {
    setReviewRows(prev => {
      const next = [...prev]
      next[index] = { ...next[index], selected: cand, included: true, manualResults: [] }
      return next
    })
  }, [])

  const handleToggleInclude = useCallback((index: number) => {
    setReviewRows(prev => {
      const next = [...prev]
      next[index] = { ...next[index], included: !next[index].included }
      return next
    })
  }, [])

  // Step 3: submit — branch by action
  const handleSubmit = async () => {
    const toSubmit = reviewRows.filter(r => r.included && r.selected)
    if (toSubmit.length === 0) return

    setStep('submitting')
    setSubmitProgress(0)

    const items: SubmittedItem[] = []

    for (let i = 0; i < toSubmit.length; i++) {
      const row = toSubmit[i]
      const cand = row.selected!
      setSubmitProgress(i + 1)

      const base = {
        appName: row.app.app_name,
        source: cand.source,
        action: cand.action,
        label: cand.ref.package_id || cand.name,
        jobId: null as string | null,
        appId: null as string | null,
        error: null as string | null,
        immediate: null as SubmittedItem['immediate'],
        duplicate: false,
      }

      try {
        if (cand.action === 'package') {
          const result = await submitJob({
            source_type: cand.source as 'winget' | 'chocolatey',
            source_reference: cand.ref.package_id!,
            deployment_methods: ['psadt', 'intune'],
            // Name the discovered app so Discovery can list this job as "that app became this
            // package"; without it the job packages the same thing but leaves no record there.
            discovery_source: {
              display_name: row.app.app_name,
              publisher: row.app.publisher,
              version: row.app.version,
              detected_app_id: row.app.detected_app_id ?? null,
            },
          })
          items.push({ ...base, jobId: result.job_id, appId: result.app_id || null })
        } else if (cand.action === 'subscribe') {
          const sub = await subscribeToApp({
            public_app_id: cand.ref.public_app_id!,
            // Name the reason and the app it came from: the library card says why the entry is
            // there, and Discovery lists it as "that app became this entry".
            origin: 'conversion',
            discovery_source: {
              display_name: row.app.app_name,
              publisher: row.app.publisher,
              version: row.app.version,
              detected_app_id: row.app.detected_app_id ?? null,
            },
          })
          items.push({ ...base, appId: cand.ref.public_app_id || sub.public_app_id, immediate: 'subscribed' })
        } else {
          // already_in_catalog — the tenant's own package, or a shortcut it already holds.
          items.push({
            ...base,
            appId: cand.ref.tenant_app_id || cand.ref.public_app_id || null,
            immediate: 'already',
          })
        }
      } catch (err: any) {
        const status = err?.response?.status
        const existingJobId = err?.response?.headers?.['x-duplicate-job-id']
        if (cand.action === 'package' && status === 409 && existingJobId) {
          items.push({ ...base, jobId: existingJobId, duplicate: true })
        } else {
          items.push({ ...base, error: errorText(err, 'Failed') })
        }
      }
    }

    setSubmittedItems(items)
    setStep('progress')

    const jobIds = items.filter(j => j.jobId).map(j => j.jobId!)
    if (jobIds.length > 0) startPolling(jobIds)
  }

  const startPolling = (jobIds: string[]) => {
    if (pollRef.current) clearInterval(pollRef.current)
    const poll = async () => {
      try {
        const statuses = await getBulkJobStatus(jobIds)
        setJobStatuses(statuses)
        const allDone = Object.values(statuses).every(s => isTerminalStatus(s.status))
        if (allDone && pollRef.current) {
          clearInterval(pollRef.current)
          pollRef.current = null
        }
      } catch {
        // ignore
      }
    }
    poll()
    pollRef.current = setInterval(poll, 5000)
  }

  const handleClose = () => {
    if (pollRef.current) clearInterval(pollRef.current)
    onClose()
  }

  if (!isOpen) return null

  const includedCount = reviewRows.filter(r => r.included && r.selected).length
  const matchedCount = reviewRows.filter(r => r.selected).length

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      <div className="relative bg-theme-card rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-theme-border-primary">
          <div>
            <h2 className="text-lg font-semibold text-theme-text-primary">Add to Catalog</h2>
            <p className="text-sm text-theme-text-secondary mt-0.5">
              {step === 'matching' && `Matching ${apps.length} app(s) across catalog, WinGet & Chocolatey...`}
              {step === 'review' && `Review matches — ${matchedCount} of ${apps.length} found`}
              {step === 'submitting' && `Processing ${submitProgress} of ${includedCount}...`}
              {step === 'progress' && 'Tracking results'}
            </p>
          </div>
          <button onClick={handleClose} className="text-theme-text-tertiary hover:text-theme-text-primary">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <PackagingEntitlementNotice />
          {step === 'matching' && <MatchingStep />}
          {step === 'review' && (
            <ReviewStep
              rows={reviewRows}
              matchError={matchError}
              onToggleInclude={handleToggleInclude}
              onManualSearch={handleManualSearch}
              onSelectCandidate={handleSelectCandidate}
            />
          )}
          {step === 'submitting' && <SubmittingStep current={submitProgress} total={includedCount} />}
          {step === 'progress' && <ProgressStep items={submittedItems} statuses={jobStatuses} onClose={handleClose} />}
        </div>

        <div className="px-6 py-4 border-t border-theme-border-primary flex justify-between items-center">
          {step === 'review' && (
            <>
              <span className="text-sm text-theme-text-secondary">
                {includedCount} of {apps.length} app(s) selected
              </span>
              <div className="flex gap-2">
                <button onClick={handleClose} className="px-4 py-2 text-sm text-theme-text-secondary hover:text-theme-text-primary">
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={includedCount === 0}
                  className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg transition-colors"
                >
                  Add {includedCount} app{includedCount !== 1 ? 's' : ''}
                </button>
              </div>
            </>
          )}
          {step === 'progress' && (
            <>
              <ProgressSummary items={submittedItems} statuses={jobStatuses} />
              <button onClick={handleClose} className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
                Done
              </button>
            </>
          )}
          {(step === 'matching' || step === 'submitting') && (
            <div className="w-full flex justify-end">
              <button onClick={handleClose} className="px-4 py-2 text-sm text-theme-text-secondary hover:text-theme-text-primary">
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// --- Sub-components ---

function MatchingStep() {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <ArrowPathIcon className="w-10 h-10 text-blue-500 animate-spin mb-4" />
      <p className="text-theme-text-primary font-medium">Matching across all sources...</p>
      <p className="text-theme-text-secondary text-sm mt-1">Public catalog → Private catalog → WinGet → Chocolatey</p>
    </div>
  )
}

function ReviewStep({
  rows,
  matchError,
  onToggleInclude,
  onManualSearch,
  onSelectCandidate,
}: {
  rows: ReviewRow[]
  matchError: string | null
  onToggleInclude: (i: number) => void
  onManualSearch: (i: number, query: string) => void
  onSelectCandidate: (i: number, c: MatchCandidate) => void
}) {
  const [manualQueries, setManualQueries] = useState<Record<number, string>>({})
  const [expandedRow, setExpandedRow] = useState<number | null>(null)

  if (matchError) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
        <p className="text-red-700 dark:text-red-400 font-medium">Match failed</p>
        <p className="text-theme-text-secondary text-sm mt-1">{matchError}</p>
      </div>
    )
  }

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-theme-border-primary">
          <th className="text-left py-2 px-2 w-8"></th>
          <th className="text-left py-2 px-2 text-theme-text-secondary font-medium">Discovered App</th>
          <th className="text-left py-2 px-2 text-theme-text-secondary font-medium">Best Match</th>
          <th className="text-left py-2 px-2 text-theme-text-secondary font-medium w-24">Match</th>
          <th className="text-left py-2 px-2 text-theme-text-secondary font-medium w-20">Change</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <ReviewRowComponent
            key={row.app.id}
            row={row}
            expanded={expandedRow === i}
            manualQuery={manualQueries[i] || ''}
            onToggleExpand={() => setExpandedRow(expandedRow === i ? null : i)}
            onToggleInclude={() => onToggleInclude(i)}
            onManualQueryChange={(q) => setManualQueries(prev => ({ ...prev, [i]: q }))}
            onManualSearch={() => onManualSearch(i, manualQueries[i] || row.app.app_name)}
            onSelectCandidate={(c) => onSelectCandidate(i, c)}
          />
        ))}
      </tbody>
    </table>
  )
}

function ReviewRowComponent({
  row,
  expanded,
  manualQuery,
  onToggleExpand,
  onToggleInclude,
  onManualQueryChange,
  onManualSearch,
  onSelectCandidate,
}: {
  row: ReviewRow
  expanded: boolean
  manualQuery: string
  onToggleExpand: () => void
  onToggleInclude: () => void
  onManualQueryChange: (q: string) => void
  onManualSearch: () => void
  onSelectCandidate: (c: MatchCandidate) => void
}) {
  const sel = row.selected
  const altCandidates = row.result.candidates.filter(c => !sel || candKey(c) !== candKey(sel))

  return (
    <>
      <tr className="border-b border-theme-border-primary hover:bg-theme-secondary">
        <td className="py-2 px-2">
          <input
            type="checkbox"
            checked={row.included}
            onChange={onToggleInclude}
            disabled={!sel}
            className="rounded border-theme-border-primary text-blue-600 focus:ring-blue-500 disabled:opacity-30"
          />
        </td>
        <td className="py-2 px-2">
          <p className="text-theme-text-primary font-medium">{row.app.app_name}</p>
          <p className="text-theme-text-tertiary text-xs">
            {row.app.publisher || ''}{row.app.version ? ` · v${row.app.version}` : ''}{row.app.arch ? ` · ${row.app.arch}` : ''}
          </p>
        </td>
        <td className="py-2 px-2">
          {sel ? (
            <div>
              <p className="text-theme-text-primary flex items-center gap-1.5">
                <SourceBadge source={sel.source} />
                {sel.name}
              </p>
              <p className="text-theme-text-tertiary text-xs">
                {sel.ref.package_id ? `${sel.ref.package_id} · ` : ''}v{sel.version || '?'}
                {sel.version_match ? ' · version match' : ''}
              </p>
              <p className={`text-xs mt-0.5 ${sel.action === 'already_in_catalog' ? 'text-green-600 dark:text-green-400' : 'text-theme-text-secondary'}`}>
                {outcomeText[sel.action]}
              </p>
            </div>
          ) : (
            <p className="text-theme-text-tertiary italic">No match found</p>
          )}
        </td>
        <td className="py-2 px-2">
          {sel ? (
            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${sel.match_type === 'exact' ? confidenceColors.high : confidenceColors.low}`}>
              {sel.match_type === 'exact' ? 'Exact' : 'Closest'}
            </span>
          ) : (
            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${confidenceColors.none}`}>none</span>
          )}
        </td>
        <td className="py-2 px-2">
          <button
            onClick={onToggleExpand}
            className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-xs flex items-center gap-1"
          >
            <MagnifyingGlassIcon className="w-3.5 h-3.5" />
            Change
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={5} className="px-2 py-3 bg-theme-secondary">
            <div className="space-y-2">
              {altCandidates.length > 0 && (
                <div>
                  <p className="text-xs text-theme-text-secondary mb-1">Other matches (priority order):</p>
                  <div className="space-y-1">
                    {altCandidates.map((c) => (
                      <CandidateOption key={candKey(c)} cand={c} selected={false} onSelect={() => onSelectCandidate(c)} />
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <input
                  type="text"
                  value={manualQuery}
                  onChange={(e) => onManualQueryChange(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && onManualSearch()}
                  placeholder={`Search WinGet + Chocolatey for "${row.app.app_name}"...`}
                  className="flex-1 px-3 py-1.5 text-sm bg-theme-card border border-theme-border-primary rounded text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <button
                  onClick={onManualSearch}
                  disabled={row.manualSearching}
                  className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded transition-colors flex items-center gap-1"
                >
                  {row.manualSearching ? <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> : <MagnifyingGlassIcon className="w-3.5 h-3.5" />}
                  Search
                </button>
              </div>

              {row.manualResults.length > 0 && (
                <div>
                  <p className="text-xs text-theme-text-secondary mb-1">Search results:</p>
                  <div className="space-y-1">
                    {row.manualResults.map((c) => (
                      <CandidateOption key={candKey(c)} cand={c} selected={!!sel && candKey(c) === candKey(sel)} onSelect={() => onSelectCandidate(c)} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

function CandidateOption({ cand, selected, onSelect }: { cand: MatchCandidate; selected: boolean; onSelect: () => void }) {
  return (
    <button
      onClick={onSelect}
      className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
        selected ? 'bg-blue-100 dark:bg-blue-900/50 border border-blue-300 dark:border-blue-700' : 'bg-theme-card hover:bg-theme-tertiary border border-transparent'
      }`}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-1.5">
          <SourceBadge source={cand.source} />
          <span className="text-theme-text-primary font-medium">{cand.name}</span>
          {cand.ref.package_id && <span className="text-theme-text-tertiary ml-1 text-xs">{cand.ref.package_id}</span>}
          {cand.match_type === 'exact' && <span className="text-green-600 dark:text-green-400 text-[10px] ml-1">exact</span>}
        </div>
        <span className="text-theme-text-tertiary text-xs">v{cand.version || '?'}</span>
      </div>
      {cand.publisher && <p className="text-theme-text-tertiary text-xs ml-[3.75rem]">{cand.publisher}</p>}
    </button>
  )
}

function SourceBadge({ source }: { source: MatchSource }) {
  const meta = sourceMeta[source]
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded shrink-0 ${meta.cls}`}>
      {meta.label}
    </span>
  )
}

function SubmittingStep({ current, total }: { current: number; total: number }) {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <ArrowPathIcon className="w-10 h-10 text-blue-500 animate-spin mb-4" />
      <p className="text-theme-text-primary font-medium">Adding to catalog...</p>
      <p className="text-theme-text-secondary text-sm mt-1">{current} of {total}</p>
      <div className="w-64 bg-theme-tertiary rounded-full h-2 mt-4">
        <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

function ProgressStep({
  items,
  statuses,
  onClose,
}: {
  items: SubmittedItem[]
  statuses: Record<string, JobStatusResponse>
  onClose: () => void
}) {
  const navigate = useNavigate()

  const statusColors: Record<string, string> = {
    pending: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
    queued: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400',
    processing: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-400',
    completed: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400',
    failed: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400',
    cancelled: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
    subscribed: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400',
    already: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
  }

  return (
    <div className="space-y-2">
      {items.map((item, idx) => {
        // Immediate (catalog) outcomes
        if (item.immediate) {
          const label = item.immediate === 'subscribed' ? 'Shortcut added' : 'Already in your library'
          const clickable = !!item.appId
          return (
            <div
              key={`${item.label}-${idx}`}
              onClick={clickable ? () => { onClose(); navigate(`/apps/${item.appId}`) } : undefined}
              className={`bg-theme-secondary rounded-lg p-4 ${clickable ? 'cursor-pointer hover:bg-theme-tertiary' : ''}`}
            >
              <div className="flex justify-between items-center">
                <div className="flex-1 min-w-0">
                  <p className="text-theme-text-primary font-medium text-sm">{item.appName}</p>
                  <p className="text-theme-text-tertiary text-xs flex items-center gap-1">
                    <SourceBadge source={item.source} />{item.label}
                  </p>
                </div>
                <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[item.immediate]}`}>{label}</span>
              </div>
              {item.error && (
                <div className="flex items-center gap-1 text-red-600 dark:text-red-400 text-xs mt-2">
                  <ExclamationCircleIcon className="w-3.5 h-3.5 shrink-0" /><span>{item.error}</span>
                </div>
              )}
              {clickable && <p className="text-green-600 dark:text-green-400 text-xs mt-1.5">View in Catalog</p>}
            </div>
          )
        }

        // Packaging-job outcomes
        const status = item.jobId ? statuses[item.jobId] : null
        const jobStatus = status?.status || (item.error ? 'failed' : 'pending')
        const progress = status?.progress_pct || 0
        const resolvedAppId = item.appId || (status?.result_app_id ?? null)
        const isCompleted = jobStatus === 'completed'
        const isFailed = jobStatus === 'failed' || !!item.error
        const isInProgress = !isCompleted && !isFailed
        const isClickable = (isCompleted && !!resolvedAppId) || isInProgress

        const handleClick = () => {
          if (isCompleted && resolvedAppId) { onClose(); navigate(`/apps/${resolvedAppId}`) }
          else if (isInProgress) { onClose(); navigate('/settings/jobs') }
        }

        return (
          <div
            key={`${item.label}-${idx}`}
            onClick={isClickable ? handleClick : undefined}
            className={`bg-theme-secondary rounded-lg p-4 transition-colors ${isClickable ? 'cursor-pointer hover:bg-theme-tertiary' : ''}`}
          >
            <div className="flex justify-between items-center">
              <div className="flex-1 min-w-0">
                <p className="text-theme-text-primary font-medium text-sm">{item.appName}</p>
                <p className="text-theme-text-tertiary text-xs flex items-center gap-1">
                  <SourceBadge source={item.source} />{item.label}
                  {item.duplicate && <span className="ml-2 text-blue-500 dark:text-blue-400">(already in progress)</span>}
                </p>
              </div>
              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[jobStatus] || statusColors.pending}`}>
                {jobStatus.charAt(0).toUpperCase() + jobStatus.slice(1)}
              </span>
            </div>
            {item.error ? (
              <div className="flex items-center gap-1 text-red-600 dark:text-red-400 text-xs mt-2">
                <ExclamationCircleIcon className="w-3.5 h-3.5 shrink-0" /><span>{item.error}</span>
              </div>
            ) : !isCompleted && (
              <div className="w-full bg-theme-tertiary rounded-full h-1.5 mt-2">
                <div className={`h-1.5 rounded-full transition-all ${jobStatus === 'failed' ? 'bg-red-500' : 'bg-blue-500'}`} style={{ width: `${progress}%` }} />
              </div>
            )}
            {isCompleted && resolvedAppId && <p className="text-green-600 dark:text-green-400 text-xs mt-1.5">View in Catalog</p>}
            {isInProgress && <p className="text-blue-600 dark:text-blue-400 text-xs mt-1.5">View in Jobs</p>}
          </div>
        )
      })}
    </div>
  )
}

function ProgressSummary({ items, statuses }: { items: SubmittedItem[]; statuses: Record<string, JobStatusResponse> }) {
  const done = items.filter(j => j.immediate || (j.jobId && statuses[j.jobId]?.status === 'completed')).length
  const failed = items.filter(j => j.error || (j.jobId && statuses[j.jobId]?.status === 'failed')).length
  const inProgress = items.length - done - failed

  return (
    <div className="flex items-center gap-3 text-sm">
      {done > 0 && (
        <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
          <CheckCircleIcon className="w-4 h-4" />{done} done
        </span>
      )}
      {failed > 0 && (
        <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
          <ExclamationCircleIcon className="w-4 h-4" />{failed} failed
        </span>
      )}
      {inProgress > 0 && (
        <span className="flex items-center gap-1 text-theme-text-secondary">
          <ArrowPathIcon className="w-4 h-4 animate-spin" />{inProgress} in progress
        </span>
      )}
    </div>
  )
}
